import React, { useEffect, useState } from "react";
import axios from "axios";
import { Fuel, TrendingDown, ShieldCheck, Phone, MapPin, ArrowRight, Star } from "lucide-react";
import { Toaster } from "sonner";
import { useSiteMeta } from "./hooks/useSiteMeta";
import { useContactInfo } from "./hooks/useContactInfo";
import { Navbar, Footer } from "./App";
import { AnimationEmbed } from "./components/AnimationEmbed";
import { useFlexfuelOffer, resolveOffer } from "./hooks/useFlexfuelOffer";
import { OfferCountdown } from "./components/OfferCountdown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_CONTENT = {
  hero: {
    badge: "Conversion Flexfuel",
    h1_line_1: "Conversion Flexfuel E85 à Clermont-Ferrand",
    h1_highlight: "— {prix_flexfuel}",
    subtitle: "Roulez à l'éthanol E85 et économisez jusqu'à 40 % sur votre budget carburant. Installation certifiée par notre équipe passionnée, à Clermont-Ferrand, sur toutes marques et modèles.",
    cta_phone_label: "06 49 07 08 20",
    cta_book: "Devis gratuit",
  },
  benefits_title_before: "Pourquoi passer au",
  benefits_title_highlight: "Flexfuel E85",
  benefits_title_after: " ?",
  benefits: [],
  articles: [],
};

const ICON_MAP = {
  "trending-down": TrendingDown,
  "shield-check": ShieldCheck,
  fuel: Fuel,
};

const FlexfuelPage = () => {
  const [businessHours, setBusinessHours] = useState([]);
  const [homeContent, setHomeContent] = useState(null);
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [reviews, setReviews] = useState({ rating: 4.8, user_ratings_total: 88 });

  const cmsSeo = content?.seo || {};
  const offer = useFlexfuelOffer();
  useSiteMeta({
    title: resolveOffer(cmsSeo.title || "Conversion Flexfuel E85 Clermont-Ferrand — dès {prix_flexfuel_court} | ATR", offer),
    description: cmsSeo.description || "Roulez à l'E85 et économisez jusqu'à 40% sur votre carburant. Conversion Flexfuel certifiée à Clermont-Ferrand (63). Toutes marques. Devis gratuit : 06 49 07 08 20.",
    canonical_path: "/conversion-flexfuel-e85",
    og_image: `${window.location.origin}/og/${offer.active ? "flexfuel" : "flexfuel-standard"}.jpg`,
    og_image_alt: "Conversion Flexfuel E85 — Auvergne Tech Racing, Clermont-Ferrand",
  });

  useEffect(() => {
    axios.get(`${API}/business-hours`).then(r => setBusinessHours(r.data)).catch(() => {});
    axios.get(`${API}/content/home_landing`).then(r => setHomeContent(r.data)).catch(() => {});
    axios.get(`${API}/content/flexfuel_page`).then(r => setContent(r.data)).catch(() => {});
    axios.get(`${API}/google-reviews`)
      .then(r => setReviews({
        rating: r.data.rating ?? 4.8,
        user_ratings_total: r.data.user_ratings_total ?? 88,
        url: r.data.url || "",
      }))
      .catch(() => {});
  }, []);

  const hero = content.hero || DEFAULT_CONTENT.hero;
  const benefits = content.benefits || [];
  const articles = content.articles || [];
  const contact = useContactInfo();

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Conversion Flexfuel E85",
    name: "Conversion Flexfuel E85 — Auvergne Tech Racing",
    description:
      "Installation d'un boîtier de conversion Flexfuel homologué permettant à votre véhicule de rouler à l'éthanol E85 (essence à base de bioéthanol).",
    provider: {
      "@type": "AutoRepair",
      name: contact.business_name,
      telephone: contact.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: contact.street,
        addressLocality: contact.city,
        postalCode: contact.postal_code,
        addressCountry: contact.country,
      },
    },
    areaServed: { "@type": "City", name: contact.city },
    offers: {
      "@type": "Offer",
      price: offer.price.toFixed(2),
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      priceValidUntil: offer.active && offer.end_date ? offer.end_date : "2027-12-31",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: reviews.rating || 4.8,
      reviewCount: reviews.user_ratings_total || 88,
      bestRating: 5,
      worstRating: 1,
    },
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="flexfuel-page">
      <script
        type="application/ld+json"
        data-testid="flexfuel-service-jsonld"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }}
      />
      <Toaster position="top-right" richColors />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF3B30]/[0.08] via-transparent to-transparent" />
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-full mb-6">
            <Fuel size={14} className="text-[#FF3B30]" />
            <span className="text-[#FF3B30] text-xs font-bold uppercase tracking-wider">{hero.badge}</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-white mb-6 leading-tight" data-testid="flexfuel-h1">
            {cmsSeo.h1_prefix || hero.h1_line_1}
            <br />
            <span className="text-[#FF3B30]">{resolveOffer(cmsSeo.h1_highlight || hero.h1_highlight, offer)}</span>
          </h1>

          <p className="text-lg md:text-xl text-white/70 leading-relaxed mb-8 max-w-3xl whitespace-pre-line">
            {hero.subtitle}
          </p>

          <OfferCountdown offer={offer} />

          <div className="flex flex-wrap gap-3 items-center mb-8">
            <a
              href={`tel:${contact.phone}`}
              data-testid="flexfuel-cta-call"
              className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-6 py-4 rounded-lg transition-colors uppercase tracking-wide text-sm"
            >
              <Phone size={16} />
              {hero.cta_phone_label}
            </a>
            <a
              href="/#booking"
              data-testid="flexfuel-cta-book"
              className="inline-flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] text-white font-semibold border border-white/20 px-6 py-4 rounded-lg transition-colors uppercase tracking-wide text-sm"
            >
              {hero.cta_book}
              <ArrowRight size={16} />
            </a>
          </div>

          <div className="flex items-center gap-2 text-white/60 text-sm">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} className="fill-yellow-400" />
              ))}
            </div>
            <span>{String(reviews.rating).replace(".", ",")}/5 — {reviews.user_ratings_total} avis Google</span>
          </div>
        </div>
      </section>

      {/* Animation Flexfuel */}
      <section className="py-12 md:py-16 bg-[#0A0A0A] border-t border-white/[0.06]" data-testid="flexfuel-page-animation-section">
        <div className="max-w-5xl mx-auto px-6">
          <AnimationEmbed src="/flexfuel.html" title="Conversion FlexFuel E85" testId="flexfuel-animation" />
        </div>
      </section>

      {/* Benefits grid */}
      {benefits.length > 0 && (
        <section className="py-16 md:py-20 bg-[#121212] border-t border-white/[0.06]">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 tracking-tight text-center">
              {content.benefits_title_before} <span className="text-[#FF3B30]">{content.benefits_title_highlight}</span>{content.benefits_title_after}
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              {benefits.map((b, i) => {
                const Icon = ICON_MAP[b.icon] || Fuel;
                return (
                  <article key={i} className="bg-black/40 border border-white/[0.06] rounded-xl p-6 hover:border-[#FF3B30]/30 transition-colors" data-testid={`flexfuel-benefit-${i}`}>
                    <Icon className="text-[#FF3B30] mb-4" size={32} />
                    <h3 className="text-white font-bold text-lg mb-2">{b.title}</h3>
                    <p className="text-white/60 text-sm leading-relaxed whitespace-pre-line">{b.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Editorial / SEO body */}
      {articles.length > 0 && (
        <section className="py-16 md:py-24 bg-[#0A0A0A] border-t border-white/[0.06]">
          <div className="max-w-4xl mx-auto px-6 space-y-12">
            {articles.map((a, i) => (
              <article key={i} data-testid={`flexfuel-article-${i}`}>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 tracking-tight">{a.title}</h2>
                <p className="text-white/70 leading-relaxed whitespace-pre-line">{a.paragraph}</p>
                {(a.cta_primary || a.cta_secondary) && (
                  <div className="flex flex-wrap gap-3 mt-6">
                    {a.cta_primary && (
                      <a
                        href="/#booking"
                        className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-6 py-3 rounded-lg transition-colors uppercase tracking-wide text-sm"
                      >
                        {a.cta_primary}
                        <ArrowRight size={16} />
                      </a>
                    )}
                    {a.cta_secondary && (
                      <a
                        href={reviews.url || "https://maps.google.com/?cid=1681708394347082412"}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="flexfuel-cta-maps"
                        className="inline-flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] text-white font-semibold border border-white/20 px-6 py-3 rounded-lg transition-colors uppercase tracking-wide text-sm"
                      >
                        <MapPin size={16} />
                        {a.cta_secondary}
                      </a>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      <Footer businessHours={businessHours} content={homeContent} />
    </div>
  );
};

export default FlexfuelPage;
